// Top of the dashboard: greeting and actions on a navy banner, with the
// featured slides (managed on the Admin page) framed on the right; the
// catalog numbers sit just below.
import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, ChevronLeft, ChevronRight, Layers, Lightbulb, Play, Plus, type LucideIcon } from "lucide-react";
import type { DashboardCounts } from "../../hooks/useDashboardData";
import type { FeaturedSlide } from "../../data/featuredContent";
import { playVideo, posterFor, VideoLightbox, type Embed } from "../VideoPlayer";
import { greeting } from "./format";

const ROTATE_MS = 8000;

function SlideLink({ link }: { link: NonNullable<FeaturedSlide["link"]> }) {
  const inner = (
    <>
      {link.label} <ArrowUpRight size={13} aria-hidden />
    </>
  );
  return link.url.startsWith("/") ? (
    <Link className="db-hero-caption-link" to={link.url}>
      {inner}
    </Link>
  ) : (
    <a className="db-hero-caption-link" href={link.url} target="_blank" rel="noopener noreferrer">
      {inner}
    </a>
  );
}

/** The featured slides: a framed screen with a caption. With several, they
 *  crossfade on a timer shown as story-style progress bars, pause on hover or
 *  focus, and can be stepped with the arrows, the bars, arrow keys or a swipe.
 *  Also used as the live preview in the banner editor. */
export function Featured({ slides }: { slides: FeaturedSlide[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [playing, setPlaying] = useState<Embed | null>(null);
  const swipeFrom = useRef<number | null>(null);
  const swiped = useRef(false);
  const count = slides.length;
  const many = count > 1;
  const reduced = Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

  // Slides can be removed while showing (the editor preview); stay in range.
  useEffect(() => {
    if (index >= count) setIndex(0);
  }, [index, count]);

  const go = useCallback((by: number) => setIndex((i) => (i + by + count) % count), [count]);
  const current = count ? index % count : 0;
  const slide = slides[current];
  if (!slide) return null;

  const watch = (s: FeaturedSlide) => {
    if (!s.videoUrl) return;
    if (s.player) setPlaying(s.player);
    else if (s.player === null) window.open(s.videoUrl, "_blank", "noopener,noreferrer");
    else setPlaying(playVideo(s.videoUrl));
  };
  // The active bar's fill animation is the timer: when it ends, the next
  // slide shows. Hover, focus and an open video pause it where it is.
  const running = many && !reduced && !paused && !playing;

  return (
    <div
      className={`db-hero-feature${many ? " db-hero-feature--many" : ""}`}
      role={many ? "region" : undefined}
      aria-roledescription={many ? "carousel" : undefined}
      aria-label={many ? "Featured" : undefined}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setPaused(false);
      }}
      onKeyDown={(e) => {
        if (!many) return;
        if (e.key === "ArrowRight") go(1);
        else if (e.key === "ArrowLeft") go(-1);
      }}
    >
      <div
        className="db-hero-stage"
        onPointerDown={(e) => {
          swipeFrom.current = e.clientX;
        }}
        onPointerUp={(e) => {
          const from = swipeFrom.current;
          swipeFrom.current = null;
          if (from == null || !many) return;
          const dx = e.clientX - from;
          if (Math.abs(dx) > 40) {
            swiped.current = true;
            go(dx < 0 ? 1 : -1);
          }
        }}
        onClickCapture={(e) => {
          // A swipe isn't also a click on the slide (which would open its video).
          if (!swiped.current) return;
          swiped.current = false;
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {slides.map((s, i) => {
          const poster = s.textOnly ? undefined : posterFor(s.videoUrl, s.image);
          const active = i === current;
          return (
            <button
              key={s.id}
              type="button"
              className={`db-hero-screen${s.textOnly ? " db-hero-screen--text" : ""}${active ? " is-active" : ""}${
                running && active ? " is-running" : ""
              }`}
              onClick={() => watch(s)}
              disabled={!s.videoUrl}
              tabIndex={active ? 0 : -1}
              aria-hidden={!active}
              aria-roledescription={many ? "slide" : undefined}
              aria-label={s.videoUrl ? `Play ${s.title}` : s.title}
              style={{ "--db-rotate": `${ROTATE_MS}ms` } as CSSProperties}
            >
              {poster ? <img src={poster} alt="" draggable={false} /> : null}
              {s.textOnly ? (
                <span className="db-hero-note">
                  {s.tag ? <span className="db-hero-note-tag">{s.tag}</span> : null}
                  <span className="db-hero-note-title">{s.title}</span>
                  {s.description ? <span className="db-hero-note-text">{s.description}</span> : null}
                </span>
              ) : null}
              {s.videoUrl ? (
                <span className="db-play" aria-hidden>
                  <Play size={18} fill="currentColor" />
                </span>
              ) : null}
            </button>
          );
        })}

        {many ? (
          <>
            <div className="db-hero-bars">
              {slides.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  className={`db-hero-bar${i < current ? " is-done" : ""}${i === current ? " is-active" : ""}`}
                  onClick={() => setIndex(i)}
                  aria-label={`Show slide ${i + 1} of ${count}: ${s.title}`}
                  aria-current={i === current}
                >
                  <span
                    // A fresh key restarts the fill each time this slide comes round.
                    key={i === current ? `run-${current}` : "idle"}
                    className="db-hero-bar-fill"
                    style={{ animationDuration: `${ROTATE_MS}ms`, animationPlayState: running ? "running" : "paused" }}
                    onAnimationEnd={i === current ? () => go(1) : undefined}
                  />
                </button>
              ))}
            </div>
            <button type="button" className="db-hero-nav db-hero-nav--prev" onClick={() => go(-1)} aria-label="Previous slide">
              <ChevronLeft size={18} aria-hidden />
            </button>
            <button type="button" className="db-hero-nav db-hero-nav--next" onClick={() => go(1)} aria-label="Next slide">
              <ChevronRight size={18} aria-hidden />
            </button>
            <span className="db-hero-count" aria-hidden>
              {current + 1} / {count}
            </span>
          </>
        ) : null}
      </div>

      <div className="db-hero-caption" key={slide.id} aria-live={running ? "off" : "polite"}>
        <span className="db-hero-tag">Featured{slide.tag ? ` · ${slide.tag}` : ""}{slide.meta ? ` · ${slide.meta}` : ""}</span>
        <span className="db-hero-caption-title">{slide.title}</span>
        {slide.link ? <SlideLink link={slide.link} /> : null}
      </div>
      {playing ? <VideoLightbox title={slide.title} embed={playing} onClose={() => setPlaying(null)} /> : null}
    </div>
  );
}

function Stat({
  to,
  icon: Icon,
  tone,
  value,
  label,
  note,
  loading,
}: {
  to: string;
  icon: LucideIcon;
  tone: string;
  value: number | undefined;
  label: string;
  note?: string;
  loading: boolean;
}) {
  return (
    <Link className={`db-stat db-stat--${tone}`} to={to}>
      <span className="db-stat-icon" aria-hidden>
        <Icon size={16} />
      </span>
      <span className="db-stat-value">{loading ? "–" : (value ?? 0).toLocaleString()}</span>
      <span className="db-stat-label">{label}</span>
      {note ? <span className="db-stat-note">{note}</span> : null}
      <ChevronRight className="db-stat-arrow" size={15} aria-hidden />
    </Link>
  );
}

export function DashHero({
  firstName,
  slides,
  slidesLoading,
  counts,
  loading,
}: {
  firstName?: string;
  slides: FeaturedSlide[];
  slidesLoading?: boolean;
  counts: DashboardCounts | null;
  loading: boolean;
}) {
  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  return (
    <>
      <section className={`db-hero${slides.length || slidesLoading ? "" : " db-hero--solo"}`} aria-label="Welcome">
        <div className="db-hero-text">
          <p className="db-hero-date">{today}</p>
          <h1 className="db-hero-hello">
            {greeting()}
            {firstName ? `, ${firstName}` : ""}
          </h1>
          <p className="db-hero-lede">What's new in AI, what it means for homebuilding, and what's on hand at Brookfield.</p>
          <div className="db-hero-actions">
            <Link className="db-hero-btn db-hero-btn--primary" to="/initiatives?new=1">
              <Plus size={16} aria-hidden /> Submit an AI idea
            </Link>
            <Link className="db-hero-btn" to="/library">
              <BookOpen size={16} aria-hidden /> Browse the library
            </Link>
          </div>
        </div>
        {slidesLoading ? (
          <div className="db-hero-feature" aria-hidden>
            <span className="db-hero-screen db-hero-screen--loading" />
          </div>
        ) : slides.length ? (
          <Featured slides={slides} />
        ) : null}
      </section>

      <nav className="db-stats" aria-label="The catalog">
        <Stat
          to="/library"
          icon={BookOpen}
          tone="library"
          value={counts?.library}
          label="AI tools & prompts"
          note={counts?.newThisMonth ? `${counts.newThisMonth} new this month` : undefined}
          loading={loading}
        />
        <Stat to="/initiatives" icon={Lightbulb} tone="initiative" value={counts?.initiatives} label="ideas from our teams" loading={loading} />
        <Stat to="/platforms" icon={Layers} tone="platform" value={counts?.platforms} label="approved AI platforms" loading={loading} />
      </nav>
    </>
  );
}
